import LinkBankAccountIcon from '@assets/images/icons-outline/link-bank-icon.svg';
import { FeatureFlagTypes } from '@generated/graphql';
import type { Integration } from '@interfaces/integrations';
import {
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Form } from '@ovComponents/2-component/form/form';
import { UserContext } from '@providers/userContextProvider';
import { Button, IconButton, Typography } from 'hephaestus/web';
import { CloseRounded as CloseRoundedIcon } from 'hephaestus/webIcons';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationFeatureFlag } from '../../../../hooks/useOrganizationFeatureFlag';
import { findIntegrationCondition } from '../findIntegrationCondition';

export type InputProp = {
  hasError: boolean;
  message: string;
  value: string;
};

const checkAutomaticBankAccountLinkingConfigurationValid = ({
  bankingIntegration,
  options,
  automatedBankAccountLinkingEnabled,
}: {
  bankingIntegration?: Integration;
  options: any;
  automatedBankAccountLinkingEnabled: boolean;
}) => {
  const autoBankAccountLinkingEnabled =
    options?.automatedBankAccountLinking || automatedBankAccountLinkingEnabled;

  if (bankingIntegration?.provider === 'FLINKS') {
    return (
      autoBankAccountLinkingEnabled &&
      bankingIntegration?.configuration?.clientIframeUrl
    );
  }

  return autoBankAccountLinkingEnabled;
};

const AddBankAccountInformation = ({
  onNext,
  onCancel,
  options,
}: {
  onNext: (index: number) => void;
  onCancel: () => void;
  options?: any;
}) => {
  const { integrations } = useContext(UserContext);
  const { t } = useTranslation(['components']);
  const automatedBankAccountLinkingEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.AutomatedBankAccountLinking,
  );
  const manuallyAddBankAccountEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.ManuallyAddBankAccount,
  );

  const bankingIntegration = integrations?.find(
    findIntegrationCondition(options),
  );

  const manualBankAccountLinkingEnabled: boolean =
    options?.manualbankAccountLinking ?? manuallyAddBankAccountEnabled;

  const autoBankAccountLinkingEnabled =
    checkAutomaticBankAccountLinkingConfigurationValid({
      bankingIntegration,
      options,
      automatedBankAccountLinkingEnabled,
    });

  return (
    <Form>
      <DialogTitle
        display='flex'
        justifyContent='space-between'
        alignItems='center'
      >
        <Typography variant='headingLarge'>
          {t('components:bankAccountSelect.information.header')}
        </Typography>
        <IconButton aria-label='close' onClick={() => onCancel()}>
          <CloseRoundedIcon fontSize='inherit' />
        </IconButton>
      </DialogTitle>
      <DialogContent
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <img
          src={LinkBankAccountIcon}
          alt='link-bank-account'
          style={{ marginBottom: 20 }}
        />

        <Typography sx={{ mb: 2.5, textAlign: 'center' }} variant='titleLarge'>
          {t('components:bankAccountSelect.information.title')}
        </Typography>
        <Typography
          sx={{ mb: 2.5, fontWeight: 500, textAlign: 'center' }}
          variant='bodyMedium'
        >
          {t('components:bankAccountSelect.information.body')}
        </Typography>
        {autoBankAccountLinkingEnabled && (
          <Button onClick={() => onNext(2)} sx={{ marginBottom: 1 }}>
            {t('components:bankAccountSelect.information.addAccountBtn')}
          </Button>
        )}
        {manualBankAccountLinkingEnabled ? (
          <Button
            onClick={() => onNext(3)}
            variant='text'
            sx={{ fontSize: '14px', fontWeight: '500' }}
          >
            {t('components:bankAccountSelect.information.addManuallyBtn')}
          </Button>
        ) : undefined}
      </DialogContent>
    </Form>
  );
};

export default AddBankAccountInformation;
